import {
  DEFAULT_QUERY_OPTIONS,
  useCreateOne,
  useGetList,
  useUpdateOne,
} from "#hooks/adapters/adapterUtils";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@validereinc/common-components";
import {
  EquipmentAdapter,
  EquipmentType,
  Resources,
} from "@validereinc/domain";
import {
  BaseError,
  dateFormatter,
  downloadLink,
  type HTTPNotFoundErrorCtx,
} from "@validereinc/utilities";
import { DEFAULT_INVALIDATE_OPTIONS } from "./adapterUtils";

const EQUIPMENT_QUERY_KEY = "equipment";

export const useGetOneEquipment = (
  id?: string,
  meta: { period?: string } = {},
  options: { enabled?: boolean } = {}
) => {
  const { localize } = useLocalization();
  const isEnabled = Boolean(id) && (options.enabled ?? true);
  return useQuery({
    queryKey: [EQUIPMENT_QUERY_KEY, id ?? "", meta] as const,
    queryFn: ({ queryKey: [_, id, meta] }) =>
      // @ts-expect-error TODO: fix filter typing
      EquipmentAdapter.getOne({ id, meta }),
    enabled: isEnabled,
    select: (resp) => resp?.data,
    meta: {
      errorCtx: {
        resource: localize("Equipment"),
        resourceId: id,
      } satisfies HTTPNotFoundErrorCtx,
      hideErrorToasts: true,
    },
    ...DEFAULT_QUERY_OPTIONS,
  });
};

export const useListEquipment = (
  params: Parameters<typeof EquipmentAdapter.getList>[0] = {},
  options: {
    select?: any;
    enabled?: boolean;
  } = {}
) =>
  useQuery({
    queryKey: [EQUIPMENT_QUERY_KEY, "getList", params] as const,
    queryFn: () => EquipmentAdapter.getList(params),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });

export const useListEquipmentTypes = useGetList(
  EquipmentAdapter.types.getList,
  Resources.EQUIPMENT_TYPE,
  "types"
);

export const useExportEquipment = (
  apiParams: Parameters<typeof EquipmentAdapter.getList>[0] = {},
  exportName?: string
) => {
  const { toast } = useToast();
  const {
    v2: { companyInfo },
  } = useAuthenticatedContext();

  const getFileName = () =>
    [
      companyInfo?.company?.name,
      exportName,
      "Equipment_List",
      dateFormatter(new Date()),
    ]
      .filter((part) => !!part)
      .join("_");

  return useMutation({
    mutationFn: async () => {
      const report = await EquipmentAdapter.exportList(apiParams);

      if (!report?.s3_download_link) {
        throw new BaseError(
          `Could not fetch download URL for equipment export`,
          { cause: report }
        );
      }

      downloadLink(report.s3_download_link, getFileName());
    },
    onError: (error) => {
      ExceptionUtils.reportException(error, "error");
      toast.push({
        intent: "error",
        description: "Failed to export equipment list.",
      });
    },
  });
};

export const useClearEquipmentCache = () => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => {
      queryClient.invalidateQueries({
        queryKey: [EQUIPMENT_QUERY_KEY],
        ...DEFAULT_INVALIDATE_OPTIONS,
      });
    },
  };
};

export const useUpdateEquipment = useUpdateOne<EquipmentType>(
  EquipmentAdapter.updateOne,
  Resources.EQUIPMENT
);

export const useCreateOneEquipment = useCreateOne<EquipmentType>(
  EquipmentAdapter.createOne,
  Resources.EQUIPMENT
);
